import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import useDelayedActiveState from 'lib/react/hooks/useDelayedActiveState'

/**
 * The `useInterface` hook
 *
 * @param {object} appData - the application data
 * @param {object} route - the current route info
 * @param {boolean} isRouteTransitioning - whether the router is transitioning
 * @returns {object} the context object
 */
function useInterface (appData, route, isRouteTransitioning) {
  const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState(false)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [headerMode, setHeaderMode] = useState('solid')
  const [isHeaderLogoDisabled, setIsHeaderLogoDisabled] = useState(false)
  const [canShowHeader, setCanShowHeader] = useState(false)
  const isHeaderActive = useDelayedActiveState(canShowHeader, 0.3, 0)

  /**
   * Effect: When route changes, check header mode
   */
  useLayoutEffect(() => {
    if (isRouteTransitioning) {
      setCanShowHeader(!!appData && route?.displayMode !== 'fullscreen')

      return
    }

    setCanShowHeader(!!appData && route?.displayMode !== 'fullscreen')
    setHeaderMode(route?.headerMode || 'fluid')
  }, [appData, isRouteTransitioning, route])

  /**
   * Effect: When transitioning the route, close the burger
   */
  useEffect(() => {
    if (!isRouteTransitioning) {
      return
    }

    setIsBurgerMenuOpen(false)
    setIsCartOpen(false)
  }, [isRouteTransitioning])

  const ctx = useMemo(
    () => ({
      headerMode,
      isBurgerMenuOpen,
      isCartOpen,
      isHeaderActive,
      isHeaderLogoDisabled,
      setIsBurgerMenuOpen,
      setIsCartOpen,
      setIsHeaderLogoDisabled
    }),
    [
      headerMode,
      isBurgerMenuOpen,
      isCartOpen,
      isHeaderActive,
      isHeaderLogoDisabled
    ]
  )

  return ctx
}

// Export
export default useInterface
